// RDS-based Compatibility Matrix Service
// This service acts as the new primary interface for compatibility data
// It fetches from RDS and falls back to the static matrix if needed

import { compatibilityMatrix as staticMatrix } from './compatibilityMatrix';

// Cache for RDS data
let rdsCompatibilityMatrix = null;
let lastFetchTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Flag to track if we're currently fetching data
let isFetching = false;

// Fetch compatibility matrix from RDS API
async function fetchCompatibilityFromRDS() {
  if (isFetching) {
    // Wait for current fetch to complete
    while (isFetching) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return rdsCompatibilityMatrix;
  }

  try {
    isFetching = true;
    // Fetching compatibility matrix from RDS
    
    // Use relative URL for EC2 deployment
    const response = await fetch('/api/config-manager?action=compatibility', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const result = await response.json();
      // The config-manager endpoint returns the data in result.data
      rdsCompatibilityMatrix = result.data;
      lastFetchTime = Date.now();
      console.log('✅ Compatibility matrix loaded from RDS');
      return rdsCompatibilityMatrix;
    } else {
      console.warn('⚠️ Failed to fetch from RDS, using static matrix');
      return staticMatrix;
    }
  } catch (error) {
    console.warn('⚠️ RDS fetch error, using static matrix:', error.message);
    console.warn('⚠️ Error details:', error);
    return staticMatrix;
  } finally {
    isFetching = false;
  }
}

// Get compatibility matrix (with caching)
async function getCompatibilityMatrix() {
  // Check if we have cached data that's still fresh
  if (rdsCompatibilityMatrix && lastFetchTime && (Date.now() - lastFetchTime) < CACHE_DURATION) {
    return rdsCompatibilityMatrix;
  }

  // Fetch fresh data from RDS
  return await fetchCompatibilityFromRDS();
}

// Helper function to get compatibility data for a connector
export async function getConnectorCompatibility(connectorId) {
  const matrix = await getCompatibilityMatrix();
  return matrix[connectorId] || null;
}

// Helper function to check if two connectors are compatible
export async function areConnectorsCompatible(connectorA, connectorB) {
  const matrix = await getCompatibilityMatrix();
  const connectorAData = matrix[connectorA];
  
  if (!connectorAData) {
    console.warn(`⚠️ No compatibility data found for connector: ${connectorA}`);
    return null;
  }

  // First check if they are explicitly incompatible
  const incompatibility = connectorAData.notCompatibleWith?.find(inc => inc && inc.id === connectorB);
  if (incompatibility) {
    return {
      compatible: false,
      reason: incompatibility.reason,
      details: incompatibility.details
    };
  }

  // Then check if they are explicitly compatible
  if (!connectorAData.compatibleWith || !Array.isArray(connectorAData.compatibleWith)) {
    return null;
  }
  
  const compatibility = connectorAData.compatibleWith.find(comp => comp && comp.id === connectorB);
  if (!compatibility) return null;

  return {
    compatible: true,
    type: compatibility.type,
    polesUsed: compatibility.polesUsed,
    warning: compatibility.warning,
    notes: compatibility.notes,
    unbalanced: compatibility.type === 'unbalanced'
  };
}

// Helper function to get all compatible connectors for a given connector
export async function getAllCompatibleConnectors(connectorId) {
  const matrix = await getCompatibilityMatrix();
  const connectorData = matrix[connectorId];
  
  if (!connectorData || !connectorData.compatibleWith || !Array.isArray(connectorData.compatibleWith)) {
    return [];
  }

  return connectorData.compatibleWith
    .filter(comp => comp && comp.id) // Filter out null/undefined entries
    .map(comp => ({
      id: comp.id,
      type: comp.type,
      polesUsed: comp.polesUsed,
      warning: comp.warning,
      notes: comp.notes,
      unbalanced: comp.type === 'unbalanced'
    }));
}

// Helper function to get compatible wire gauges for a connector
export async function getConnectorWireGaugeCompatibility(connectorId) {
  const matrix = await getCompatibilityMatrix();
  const connectorData = matrix[connectorId];
  
  if (!connectorData || !connectorData.wireGaugeCompatibility) {
    // Fall back to static matrix if no RDS data
    const staticConnectorData = staticMatrix[connectorId];
    if (staticConnectorData && staticConnectorData.wireGaugeCompatibility) {
      return staticConnectorData.wireGaugeCompatibility;
    }
    return [];
  }
  return connectorData.wireGaugeCompatibility;
}

// Helper function to get compatible wire types for a connector
export async function getConnectorWireCompatibility(connectorId) {
  const matrix = await getCompatibilityMatrix();
  const connectorData = matrix[connectorId];
  
  if (!connectorData || !connectorData.wireCompatibility) {
    // Fall back to static matrix if no RDS data
    const staticConnectorData = staticMatrix[connectorId];
    if (staticConnectorData && staticConnectorData.wireCompatibility) {
      return staticConnectorData.wireCompatibility;
    }
    return [];
  }
  return connectorData.wireCompatibility;
}

// Update compatibility entry (admin function)
export async function updateCompatibilityEntry(connectorA, connectorB, data, reason = 'Admin update') {
  try {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=compatibility', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        connectorA,
        connectorB,
        ...data,
        reason
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      rdsCompatibilityMatrix = null;
      lastFetchTime = null;
      console.log(`✅ Updated compatibility: ${connectorA} <-> ${connectorB}`);
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update compatibility:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating compatibility:', error);
    return false;
  }
}

// Initialize the cache by fetching data
export async function initializeCompatibilitySystem() {
  console.log('🔧 Initializing RDS-based compatibility system...');
  try {
    await fetchCompatibilityFromRDS();
    console.log('🚀 RDS-based compatibility system ready!');
  } catch (error) {
    console.warn('⚠️ RDS initialization warning (falling back to static):', error.message);
  }
}

// Force refresh from RDS (for admin use)
export async function refreshCompatibilityMatrix() {
  console.log('🔄 Force refreshing compatibility matrix from RDS...');
  rdsCompatibilityMatrix = null;
  lastFetchTime = null;
  return await fetchCompatibilityFromRDS();
}

// Export the matrix data directly (for backward compatibility)
export async function getFullCompatibilityMatrix() {
  return await getCompatibilityMatrix();
}

// Health check
export async function checkCompatibilitySystemHealth() {
  try {
    const response = await fetch('/api/config-manager?action=health');
    if (response.ok) {
      const health = await response.json();
      return {
        status: 'healthy',
        source: rdsCompatibilityMatrix ? 'RDS' : 'static',
        lastFetch: lastFetchTime ? new Date(lastFetchTime).toISOString() : 'never',
        cacheValid: rdsCompatibilityMatrix && lastFetchTime && (Date.now() - lastFetchTime) < CACHE_DURATION,
        rdsHealth: health
      };
    }
    return { status: 'unhealthy', source: 'static' };
  } catch (error) {
    return { status: 'error', source: 'static', error: error.message };
  }
}